// src/components/usuario/DashboardUsuario.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE, UPLOAD_ENDPOINT, SERVER_URL } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";
import { personasNormalize } from "../../../General/Objetos/PersonasModel";
import { AvisoModal, NoticiaModal, ArticuloModal, EventoModal } from './DashboardModals';
import EncuestaModal from './EncuestaModal';
import ForoCompletoModal from './ForoCompletoModal';
import { FiStar, FiFileText, FiMessageSquare, FiAlertTriangle, FiInfo, FiBell, FiClock } from "react-icons/fi";
import { parseDateTimeFlexible, formatTimeRange } from "../../../../utils/DateHelpers";
import { useLogos } from "../../../../context/LogoProvider";

// Helper para construir la URL completa de la imagen (mismo que DashboardComunicacion)
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const baseUrl = API_BASE.replace(/\/api\/?$/, "");
    return `${baseUrl}${path}`;
};

// Placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect fill='%23E5E7EB' width='400' height='250'/%3E%3Cpath fill='%239CA3AF' d='M163 142.5l24-24 24 24 48-48 48 48V187H163v-44.5zm48-39.5c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z'/%3E%3C/svg%3E";


const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Ccircle fill='%23BFDBFE' cx='40' cy='40' r='40'/%3E%3Cpath fill='%233B82F6' d='M40 44c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16zm0 4c10.7 0 32 5.4 32 16v12H8V64c0-10.6 21.3-16 32-16z'/%3E%3C/svg%3E";

// Helpers para fechas
const MESES_ABREV = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const mapNivelPrioridad = (tipoAviso) => {
    const nivel = String(tipoAviso || "").toLowerCase();
    if (nivel === "1" || nivel === "alta") return "alta";
    if (nivel === "2" || nivel === "media") return "media";
    return "baja";
};

// Helpers para estilos de categoria (mismos que en Calendario)
const getCategoriaClass = (categoria) => {
    if (!categoria) return "corporativo";
    const cat = categoria.toString().trim().toLowerCase();

    if (cat.includes("capacita")) return "capacitacion";
    if (cat === "rh" || cat.includes("recursos")) return "rh";
    if (cat.includes("finanza")) return "finanzas";
    if (cat.includes("auditor")) return "auditoria";
    if (cat.includes("cumple") || cat.includes("birthday")) return "cumpleanos";
    if (cat.includes("festiv") || cat.includes("holiday")) return "festivo";

    return "corporativo";
};

const getCategoriaStyles = (catName) => {
    const cls = getCategoriaClass(catName);
    switch (cls) {
        case 'corporativo': return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
        case 'capacitacion': return { bg: '#FFEDD5', text: '#9A3412', border: '#F97316' };
        case 'rh': return { bg: '#F3E8FF', text: '#6B21A8', border: '#8B5CF6' };
        case 'finanzas': return { bg: '#DCFCE7', text: '#166534', border: '#10B981' };
        case 'auditoria': return { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
        case 'cumpleanos': return { bg: '#FCE7F3', text: '#9D174D', border: 'var(--color-primary)' };
        case 'festivo': return { bg: '#CCFBF1', text: '#0F766E', border: '#14B8A6' };
        default: return { bg: '#F1F5F9', text: '#475569', border: '#94A3B8' };
    }
};

const parseDiaMes = (fechaStr) => {
    if (!fechaStr) return { dia: "--", mes: "--" };
    const d = parseDateTimeFlexible(fechaStr);
    if (!d) return { dia: "--", mes: "--" };
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = MESES_ABREV[d.getMonth()] ?? "--";
    return { dia, mes: mes.toUpperCase() }; // Uppercase month 
};

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return "";
    const d = parseDateTimeFlexible(fechaStr);
    if (!d) return fechaStr;
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = MESES_ABREV[d.getMonth()] ?? "";
    const anio = d.getFullYear();
    return `${dia} ${mes} ${anio} `;
};

// Helper para obtener el rango de la semana actual (Lunes a Domingo)
const getRangoSemanaActual = () => {
    const hoy = new Date();
    const diaSemana = hoy.getDay(); // 0 (Dom) a 6 (Sab)

    // Ajustar para que el Lunes sea el primer día (1) y Domingo el último (7 o 0)
    // Si es domingo (0), el lunes fue hace 6 días. Si es lunes (1), el lunes es hoy.
    const diffLunes = hoy.getDate() - (diaSemana === 0 ? 6 : diaSemana - 1);
    const lunes = new Date(hoy.setDate(diffLunes));
    lunes.setHours(0, 0, 0, 0);

    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    domingo.setHours(23, 59, 59, 999);

    return { inicio: lunes, fin: domingo };
};

const DashboardUsuario = ({ onNavigate }) => {
    const { getSystemImageUrl } = useLogos();
    // Navigation State
    const navigate = useNavigate();
    // No vistaActual state anymore, always show dashboard grid


    const [noticias, setNoticias] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [encuestas, setEncuestas] = useState([]);
    const [foros, setForos] = useState([]);
    const [avisos, setAvisos] = useState([]);
    const [sugerencias, setSugerencias] = useState([]);
    const [articulos, setArticulos] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [cumpleanosData, setCumpleanosData] = useState([]); // Nuevo estado para datos reales
    const [birthdayIndex, setBirthdayIndex] = useState(0); // ?? Indice para carrusel
    const [forumIndex, setForumIndex] = useState(0); // ?? Indice para carrusel de foros
    const [forosCarousel, setForosCarousel] = useState([]); // Lista ordenada para el carrusel
    const [currentUserId, setCurrentUserId] = useState(null);

    // Pagination States for Vertical Carousels
    const [avisosIndex, setAvisosIndex] = useState(0);
    const [noticiasIndex, setNoticiasIndex] = useState(0);
    const [articulosIndex, setArticulosIndex] = useState(0);

    const [avisosDir, setAvisosDir] = useState('next');
    const [noticiasDir, setNoticiasDir] = useState('next');
    const [articulosDir, setArticulosDir] = useState('next');
    const [eventosIndex, setEventosIndex] = useState(0); // Index for Eventos Carousel

    // Modal states for viewing items
    const [selectedAviso, setSelectedAviso] = useState(null);
    const [selectedNoticia, setSelectedNoticia] = useState(null);
    const [selectedArticulo, setSelectedArticulo] = useState(null);
    const [selectedForo, setSelectedForo] = useState(null);
    const [selectedEncuesta, setSelectedEncuesta] = useState(null);

    const [selectedEvento, setSelectedEvento] = useState(null);


    // Carga inicial desde la BD
    useEffect(() => {
        const cargarDatos = async () => {
            const idUsr = getSessionUserId();
            if (!idUsr) return;
            setCurrentUserId(idUsr);

            let areasMap = {};

            try {
                // 1. Obtener Áreas de Negocio y Estados Intranet para mapeos y Categorías
                const [resAreas, resEstados, resCategorias] = await Promise.all([
                    axios.get(ENDPOINTS.areasNegocios).catch(() => ({ data: [] })),
                    axios.get(ENDPOINTS.estadosIntranet).catch(() => ({ data: [] })),
                    axios.get(ENDPOINTS.categorias).catch(() => ({ data: [] }))
                ]);

                if (Array.isArray(resAreas.data)) {
                    resAreas.data.forEach(a => areasMap[a.idAreaNegocio || a.IdAreaNegocio] = a.nombre || a.Nombre || a.descripcion || a.Descripcion);
                }

                const estadosMap = {};
                if (Array.isArray(resEstados.data)) {
                    resEstados.data.forEach(e => estadosMap[e.idEstado || e.IdEstado] = e.nombre || e.Nombre);
                }

                const cats = resCategorias.data || [];
                const categoriesMap = {};
                cats.forEach(c => categoriesMap[c.idCategoria || c.IdCategoria] = c.nombre || c.Nombre);

                // 2. Cumpleaños
                try {
                    const resCumpleanos = await axios.get(ENDPOINTS.personasCumpleanosDashboardByUsuarioId(idUsr));
                    const listaPersonas = (resCumpleanos.data || []).map(p => personasNormalize(p));

                    const hoy = new Date();
                    hoy.setHours(0, 0, 0, 0);

                    if (listaPersonas.length > 0) {
                        const mapped = listaPersonas.map(p => {
                            const f = new Date(p.fechaNacimiento);
                            const mes = f.getUTCMonth();
                            const dia = f.getUTCDate();
                            const thisYear = new Date(hoy.getFullYear(), mes, dia);
                            return { ...p, thisYear, mes, dia };
                        }).sort((a, b) => a.thisYear - b.thisYear);

                        let centerIdx = mapped.findIndex(x => x.thisYear >= hoy);
                        if (centerIdx === -1) centerIdx = 0;

                        const formattedAll = mapped.map(p => ({
                            id: p.idPersona,
                            nombre: p.nombre,
                            titulo: `${p.nombre} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''} `.trim(),
                            fotoPerfil: p.fotoPerfil,
                            dia: p.dia,
                            mes: p.mes,
                        }));

                        setCumpleanosData(formattedAll);
                        setBirthdayIndex(centerIdx);
                    } else {
                        setCumpleanosData([]);
                    }
                } catch (e) { console.error("Error cumpleaños:", e); }

                // 3. Noticias
                try {
                    const resNoticias = await axios.get(ENDPOINTS.noticiasDashboardByUsuarioId(idUsr));
                    const noticiasTransformadas = resNoticias.data.map(noticia => ({
                        id: noticia.idNoticia ?? noticia.IdNoticia,
                        titulo: noticia.titulo ?? noticia.Titulo,
                        resumen: noticia.resumen ?? noticia.Resumen,
                        contenido: noticia.contenido ?? noticia.Contenido,
                        fechaPublicacion: noticia.fechaPublicacion ?? noticia.FechaPublicacion,
                        imagen: noticia.imagenUrl ?? noticia.ImagenUrl,
                        destacada: noticia.destacada ?? noticia.Destacada,
                        area: noticia.nombreArea ?? noticia.NombreArea ?? areasMap[noticia.idAreaNegocio ?? noticia.IdAreaNegocio] ?? "Corporativo",
                        categoria: noticia.nombreCategoria ?? noticia.NombreCategoria ?? categoriesMap[noticia.idCategoria ?? noticia.IdCategoria] ?? "General",
                        estado: (noticia.nombreEstado ?? noticia.NombreEstado ?? "borrador").toLowerCase(),
                        tipoNoticia: (noticia.tipoNoticia ?? noticia.TipoNoticia) || "Baja",
                        autor: noticia.nombreAutor ?? noticia.NombreAutor ?? "Experto Corporativo"
                    }));
                    setNoticias(noticiasTransformadas);
                } catch (e) { console.error("Error noticias:", e); }

                // 4. Eventos
                try {
                    const resEventos = await axios.get(ENDPOINTS.eventosDashboardByUsuarioId(idUsr));
                    const dataEventos = resEventos.data || [];

                    const eventosTransformados = dataEventos.map(evento => {
                        const catObj = cats.find(c => (c.idCategoria ?? c.IdCategoria) === (evento.idCategoria ?? evento.IdCategoria));
                        let catName = catObj ? (catObj.nombre ?? catObj.Nombre) : (evento.nombreCategoria ?? evento.NombreCategoria ?? evento.categoria ?? evento.Categoria ?? "Corporativo");

                        return {
                            id: evento.idEvento ?? evento.IdEvento ?? 0,
                            titulo: evento.titulo ?? evento.Titulo ?? "Sin título",
                            descripcion: evento.descripcion ?? evento.Descripcion ?? "",
                            fechaInicio: evento.fechaInicio ?? evento.FechaInicio,
                            fechaFin: evento.fechaFin ?? evento.FechaFin,
                            categoria: catName,
                            ubicacion: evento.ubicacion ?? evento.Ubicacion ?? ""
                        };
                    });

                    // Filtrar solo eventos próximos (que no han terminado hoy)
                    const hoy = new Date();
                    hoy.setHours(0, 0, 0, 0);
                    const eventosProximos = eventosTransformados.filter(evento => {
                        const fFin = evento.fechaFin ? new Date(evento.fechaFin) : new Date(evento.fechaInicio);
                        return fFin >= hoy;
                    }).sort((a, b) => new Date(a.fechaInicio) - new Date(b.fechaInicio));

                    setEventos(eventosProximos);
                } catch (e) { console.error("Error eventos:", e); }

                // 5. Encuestas
                try {
                    const resEncuestas = await axios.get(ENDPOINTS.encuestasDashboardByUsuarioId(idUsr));

                    const encuestasTransformadas = resEncuestas.data.map(encuesta => ({
                        id: Number(encuesta.idEncuesta ?? encuesta.IdEncuesta ?? 0),
                        idEncuesta: Number(encuesta.idEncuesta ?? encuesta.IdEncuesta ?? 0),
                        titulo: encuesta.titulo ?? encuesta.Titulo ?? 'Encuesta',
                        descripcion: encuesta.descripcion ?? encuesta.Descripcion,
                        fechaCreacion: null, // Si no viene, null
                        fechaCierre: null, // Si no viene, null
                        estado: (encuesta.esActiva ?? encuesta.EsActiva) ? "activa" : "cerrada",
                        respondidaUsuario: encuesta.respondidaUsuario ?? encuesta.RespondidaUsuario ?? false,
                        respondida: encuesta.respondidaUsuario ?? encuesta.RespondidaUsuario ?? false,
                        nombreArea: encuesta.nombreArea ?? encuesta.NombreArea ?? "General"
                    }));

                    setEncuestas(encuestasTransformadas);
                } catch (e) { console.error("Error encuestas:", e); }

                // 6. Foros
                try {
                    const [resForos, resRespuestas] = await Promise.all([
                        axios.get(ENDPOINTS.forosDashboardByUsuarioId(idUsr)),
                        axios.get(ENDPOINTS.forosRespuestas).catch(() => ({ data: [] }))
                    ]);

                    const replyCounts = {};
                    (resRespuestas.data || []).forEach(r => {
                        const fid = r.idForo || r.IdForo;
                        if (fid) {
                            replyCounts[fid] = (replyCounts[fid] || 0) + 1;
                        }
                    });

                    const forosTransformados = resForos.data.map(foro => ({
                        id: foro.idForo ?? foro.IdForo,
                        titulo: foro.titulo ?? foro.Titulo,
                        descripcion: foro.descripcionInicial ?? foro.DescripcionInicial ?? foro.descripcion ?? foro.Descripcion,
                        categoria: (foro.categoria ?? foro.Categoria) || "General",
                        fecha: foro.fechaCreacion ?? foro.FechaCreacion,
                        imagen: foro.imagenUrl ?? foro.ImagenUrl,
                        activo: foro.activo ?? foro.Activo,
                        estado: (estadosMap[foro.idEstado ?? foro.IdEstado] || "").toLowerCase() || (foro.activo ?? foro.Activo ? "abierto" : "cerrado"),
                        respuestas: replyCounts[foro.idForo ?? foro.IdForo] || 0,
                        destacado: foro.destacado || foro.destacada || foro.Destacado || foro.Destacada || false
                    }));

                    setForos(forosTransformados);

                    const forosAbiertos = forosTransformados.filter(f => f.estado === "abierto");
                    const getTime = (f) => new Date(f.fecha || 0).getTime();
                    const ranked = [...forosAbiertos].sort((a, b) => {
                        if (b.respuestas !== a.respuestas) return b.respuestas - a.respuestas;
                        return getTime(b) - getTime(a);
                    });

                    if (ranked.length > 0) {
                        const center = ranked[0]; const left1 = ranked[1]; const right1 = ranked[2];
                        const others = ranked.slice(3).sort((a, b) => getTime(b) - getTime(a));
                        const farLeft = others.slice(0, 2); const farRight = others.slice(2, 4);
                        const list = [];
                        if (farLeft[1]) list.push(farLeft[1]);
                        if (farLeft[0]) list.push(farLeft[0]);
                        if (left1) list.push(left1);
                        list.push(center);
                        if (right1) list.push(right1);
                        if (farRight[0]) list.push(farRight[0]);
                        if (farRight[1]) list.push(farRight[1]);

                        setForosCarousel(list);
                        setForumIndex(list.indexOf(center));
                    } else {
                        setForosCarousel([]);
                    }
                } catch (e) { console.error("Error foros:", e); }

                // 7. Avisos
                try {
                    const resAvisos = await axios.get(ENDPOINTS.avisosDashboardByUsuarioId(idUsr));
                    const avisosTransformados = resAvisos.data.map(aviso => ({
                        id: aviso.idAviso ?? aviso.IdAviso,
                        titulo: aviso.titulo ?? aviso.Titulo,
                        mensaje: aviso.mensaje ?? aviso.Mensaje ?? aviso.contenido ?? aviso.Contenido,
                        fechaInicio: aviso.fechaInicio ?? aviso.FechaInicio,
                        fechaFin: aviso.fechaFin ?? aviso.FechaFin,
                        fecha: aviso.fechaInicio ?? aviso.FechaInicio ?? aviso.fecha ?? aviso.Fecha,
                        nivel: mapNivelPrioridad(aviso.tipoAviso ?? aviso.TipoAviso ?? aviso.idTipoAviso ?? aviso.IdTipoAviso),
                        area: areasMap[aviso.idAreaNegocio ?? aviso.IdAreaNegocio] || "General",
                        activo: aviso.activo ?? aviso.Activo,
                        mostrarEnDashboard: aviso.mostrarEnDashboard ?? aviso.MostrarEnDashboard,
                        imagen: aviso.imagenUrl ?? aviso.ImagenUrl,
                        destacada: mapNivelPrioridad(aviso.tipoAviso ?? aviso.TipoAviso ?? aviso.idTipoAviso ?? aviso.IdTipoAviso) === 'alta'
                    }));
                    setAvisos(avisosTransformados);
                } catch (e) { console.error("Error avisos:", e); }

                // 8. Sugerencias
                try {
                    // Endpoint sends all filtered by user company
                    const response = await axios.get(ENDPOINTS.sugerenciasDashboardByUsuarioId(idUsr));
                    setSugerencias(response.data);
                } catch (e) { console.error("Error sugerencias:", e); }

                // 9. Articulos
                try {
                    const response = await axios.get(ENDPOINTS.articulosDashboardByUsuarioId(idUsr));
                    const articulosTransformados = response.data.map(articulo => ({
                        id: articulo.idArticulo ?? articulo.IdArticulo,
                        titulo: articulo.titulo ?? articulo.Titulo,
                        resumen: articulo.resumen ?? articulo.Resumen,
                        contenido: articulo.contenido ?? articulo.Contenido,
                        fechaPublicacion: articulo.fechaPublicacion ?? articulo.FechaPublicacion,
                        imagen: articulo.imagenUrl ?? articulo.ImagenUrl,
                        area: articulo.nombreArea ?? articulo.NombreArea ?? areasMap[articulo.idAreaNegocio ?? articulo.IdAreaNegocio] ?? "Corporativo",
                        categoria: articulo.nombreCategoria ?? articulo.NombreCategoria ?? categoriesMap[articulo.idCategoria ?? articulo.IdCategoria] ?? "General",
                        autor: articulo.nombreAutor ?? articulo.NombreAutor ?? articulo.autor ?? articulo.Autor ?? "Experto Corporativo",
                        destacado: articulo.destacada || articulo.destacado || articulo.Destacada || articulo.Destacado || false
                    }));
                    setArticulos(articulosTransformados);
                } catch (e) { console.error("Error articulos:", e); }

                // 10. Documentos
                try {
                    const resDocs = await axios.get(ENDPOINTS.documentosDashboardByUsuarioId(idUsr));
                    setDocumentos(resDocs.data);
                } catch (e) { console.error("Error documentos:", e); }

            } catch (error) {
                console.error("Error general cargando datos DashboardUsuario:", error);
            }
        };

        cargarDatos();
    }, []);

    // KPIs para botones (Vista Usuario: LO QUE PUEDO VER Y ESTÁ ACTIVO)
    const totalNoticias = useMemo(() => noticias.filter(n => n.estado === "publicada" || n.estado === "activo").length, [noticias]);

    const totalEventos = eventos.length; // Ya están filtrados por "próximos" en cargarDatos

    const encuestasActivas = useMemo(() => {
        // Count all surveys that are active, even if already answered
        return encuestas.filter(e => e.estado === "activa").length;
    }, [encuestas]);

    const resumenBuzon = useMemo(() => {
        if (!Array.isArray(sugerencias) || sugerencias.length === 0) {
            return { total: 0, atendidas: 0 };
        }
        // Filtrar para contar ÚNICAMENTE mis sugerencias
        // Nota: En el backend el campo es idUsuarioAutor o IdUsuarioAutor
        const misSugerencias = sugerencias.filter(s =>
            (s.idUsuarioAutor || s.IdUsuarioAutor || s.idUsuario || s.IdUsuario) == currentUserId
        );
        const total = misSugerencias.length;
        const atendidas = misSugerencias.filter((s) => s.estado === "atendida").length;
        return { total, atendidas };
    }, [sugerencias, currentUserId]);

    const totalArticulos = articulos.length;
    const totalForos = useMemo(() => foros.filter(f => f.estado === "abierto").length, [foros]);
    const totalDocumentos = documentos.length;

    const avisosDashboardCount = useMemo(() => {
        return avisos.filter((a) => a.activo !== false).length;
    }, [avisos]);

    const avisosActivos = useMemo(() => {
        return avisos.filter((a) => a.activo !== false);
    }, [avisos]);

    // Avisos (Sorted All)
    const avisosOrdenados = useMemo(() => {
        if (!Array.isArray(avisosActivos)) return [];
        return [...avisosActivos].sort((a, b) => {
            // 1. Urgency priority
            const niveles = { 'alta': 1, 'media': 2, 'baja': 3 };
            const nivelA = niveles[a.nivel] || 3;
            const nivelB = niveles[b.nivel] || 3;

            if (nivelA !== nivelB) return nivelA - nivelB;

            // 2. Date priority
            const fA = new Date(a.fechaInicio || a.fecha).getTime();
            const fB = new Date(b.fechaInicio || b.fecha).getTime();
            return fB - fA;
        });
    }, [avisosActivos]);

    // Noticias (Sorted All)
    const noticiasOrdenadas = useMemo(() => {
        if (!Array.isArray(noticias)) return [];
        return [...noticias].sort((a, b) => {
            // 1. Urgency priority
            const niveles = { 'Alta': 1, 'Media': 2, 'Baja': 3 };
            const nivelA = niveles[a.tipoNoticia] || 3;
            const nivelB = niveles[b.tipoNoticia] || 3;

            if (nivelA !== nivelB) return nivelA - nivelB;

            // 2. Date priority
            const fA = new Date(a.fechaPublicacion || a.fecha).getTime();
            const fB = new Date(b.fechaPublicacion || b.fecha).getTime();
            return fB - fA;
        });
    }, [noticias]);

    // Articulos (Sorted All)
    const articulosOrdenados = useMemo(() => {
        if (!Array.isArray(articulos)) return [];
        return [...articulos].sort((a, b) => {
            const fA = new Date(a.fechaPublicacion || a.fecha).getTime();
            const fB = new Date(b.fechaPublicacion || b.fecha).getTime();
            return fB - fA;
        });
    }, [articulos]);

    // Proximos eventos (3) - Logica mejorada: solo futuros
    // Proximos eventos (All Sorted by Date for Carousel)
    const eventosProximos = useMemo(() => {
        if (!Array.isArray(eventos)) return [];

        // Sort all events chronologically (Past -> Future)
        const ordenados = [...eventos].sort((a, b) => {
            const fA = new Date(a.fechaInicio || a.fecha).getTime();
            const fB = new Date(b.fechaInicio || b.fecha).getTime();
            return fA - fB;
        });
        return ordenados;
    }, [eventos]);

    // Initialize Event Index to Nearest Upcoming Event
    useEffect(() => {
        if (eventosProximos.length > 0) {
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);

            // Find first event that is today or future
            const firstFutureIndex = eventosProximos.findIndex(e => {
                const f = new Date(e.fechaInicio || e.fecha);
                return f >= hoy;
            });

            if (firstFutureIndex !== -1) {
                setEventosIndex(firstFutureIndex);
            } else {
                // If all are past, show the last one (most recent past)
                setEventosIndex(eventosProximos.length - 1);
            }
        }
    }, [eventosProximos]);

    // Eventos Carousel Data (Active Triplet)
    // Eventos Carousel Data (Active Triplet) - NO LOOP
    const eventosCarouselDisplay = useMemo(() => {
        if (!eventosProximos || eventosProximos.length === 0) return [];

        const current = eventosIndex;
        // If current is 0, prev is null. If current is last, next is null.
        const prevItem = current > 0 ? eventosProximos[current - 1] : null;
        const currentItem = eventosProximos[current];
        const nextItem = current < eventosProximos.length - 1 ? eventosProximos[current + 1] : null;

        return [prevItem, currentItem, nextItem];
    }, [eventosProximos, eventosIndex]);

    // Cumpleanos (Carousel Logic & Handlers)
    const cumpleanos = useMemo(() => {
        if (!cumpleanosData || cumpleanosData.length === 0) return [];
        const len = cumpleanosData.length;

        const current = birthdayIndex % len; // safety
        const prev = (current - 1 + len) % len;
        const next = (current + 1) % len;

        // Always return array of 3: [Left, Center, Right]
        return [cumpleanosData[prev], cumpleanosData[current], cumpleanosData[next]];
    }, [cumpleanosData, birthdayIndex]);

    const handlePrevBirthday = () => {
        if (!cumpleanosData.length) return;
        setBirthdayIndex(prev => (prev - 1 + cumpleanosData.length) % cumpleanosData.length);
    };

    const handleNextBirthday = () => {
        if (!cumpleanosData.length) return;
        setBirthdayIndex(prev => (prev + 1) % cumpleanosData.length);
    };

    // Eventos Handlers
    // Eventos Handlers - NO LOOP
    const handleNextEvent = () => {
        if (!eventosProximos.length) return;
        setEventosIndex(prev => {
            if (prev < eventosProximos.length - 1) return prev + 1;
            return prev;
        });
    };

    const handlePrevEvent = () => {
        if (!eventosProximos.length) return;
        setEventosIndex(prev => {
            if (prev > 0) return prev - 1;
            return prev;
        });
    };

    // Foros Handlers
    const handlePrevForum = () => {
        if (!forosCarousel.length) return;
        setForumIndex(prev => (prev - 1 + forosCarousel.length) % forosCarousel.length);
    };

    const handleNextForum = () => {
        if (!forosCarousel.length) return;
        setForumIndex(prev => (prev + 1) % forosCarousel.length);
    };

    // Encuestas (3)
    const encuestasRecientes = useMemo(() => {
        if (!Array.isArray(encuestas)) return [];
        // Filter to show only active surveys in the dashboard list
        return encuestas
            .filter(e => e.estado === "activa")
            .slice(0, 5)
            .map((e) => {
                let status;
                if (e.respondida) {
                    status = "contestada";
                } else if (e.estado === "activa") {
                    status = "pendiente";
                } else {
                    status = "cerrada";
                }

                const fechaLimite = e.fechaLimite || e.fechaCierre || e.fechaCreacion || "";
                return {
                    id: e.id,
                    titulo: e.titulo,
                    status,
                    fechaLimite: fechaLimite ? formatFechaCorta(fechaLimite) : "Sin fecha",
                    // original for modal
                    ...e
                };
            });
    }, [encuestas]);

    // Foros (3)
    /* const forosRecientes = useMemo(() => {
        if (!Array.isArray(foros)) return [];
        return foros.slice(0, 3);
    }, [foros]); */

    // Helper para navegar
    const handleNavegarA = (vista, item = null) => {
        if (vista === "dashboard") {
            navigate("/menu/intranet/usuario");
        } else {
            navigate(`/menu/intranet/usuario/${vista}`, { state: { initialItem: item } });
        }
    };

    // Generic Handlers for Vertical Pagination
    const handlePrevPage = (setter, current, step = 3) => {
        setter(prev => Math.max(0, prev - step));
    };
    const handleNextPage = (setter, current, total, step = 3) => {
        if (current + step < total) {
            setter(prev => prev + step);
        }
    };



    return (
        <div className="intra-dashboard" style={{ padding: '20px' }}>
            {/* HEADER */}


            {/* 7 ACTION BUTTONS */}
            {/* 8 VISUAL NAV CARDS - REDESIGNED: Image Background, Grid Layout */}
            <section className="intra-dashboard-metrics">
                {[
                    {
                        title: "Noticias",
                        subtitle: "Mantente informado",
                        value: totalNoticias,
                        onClick: () => handleNavegarA("noticias"),
                        image: getSystemImageUrl("IntranetNoticias", "/intranet/noticias.jpeg")
                    },
                    {
                        title: "Eventos",
                        subtitle: "Calendario corporativo",
                        value: totalEventos,
                        onClick: () => handleNavegarA("calendario"),
                        image: getSystemImageUrl("IntranetEventos", "/intranet/eventos.jpeg")
                    },
                    {
                        title: "Encuestas",
                        subtitle: "Tu opinión cuenta",
                        value: encuestasActivas,
                        onClick: () => handleNavegarA("encuestas"),
                        image: getSystemImageUrl("IntranetEncuestas", "/intranet/encuestas.jpeg")
                    },
                    {
                        title: "Buzón",
                        subtitle: "Ideas y sugerencias",
                        value: resumenBuzon.total,
                        onClick: () => handleNavegarA("sugerencias"),
                        image: getSystemImageUrl("IntranetBuzon", "/intranet/buzon.jpeg")
                    },
                    {
                        title: "Avisos",
                        subtitle: "Comunicados importantes",
                        value: avisosDashboardCount,
                        onClick: () => handleNavegarA("avisos"),
                        image: getSystemImageUrl("IntranetAvisos", "/intranet/avisos.jpeg")
                    },
                    {
                        title: "Artículos",
                        subtitle: "Blog y conocimiento",
                        value: totalArticulos,
                        onClick: () => handleNavegarA("articulos"),
                        image: getSystemImageUrl("IntranetArticulos", "/intranet/articulos.jpeg")
                    },
                    {
                        title: "Foros",
                        subtitle: "Espacios de discusión",
                        value: totalForos,
                        onClick: () => handleNavegarA("foros"),
                        image: getSystemImageUrl("IntranetForos", "/intranet/foros.jpeg")
                    },
                    {
                        title: "Documentos",
                        subtitle: "Gestión documental",
                        value: totalDocumentos,
                        onClick: () => handleNavegarA("documentos"),
                        image: getSystemImageUrl("IntranetDocumentos", "/intranet/documentos.jpeg")
                    },
                ].map((item, index) => (
                    <div
                        key={index}
                        className="intra-visual-card"
                        onClick={item.onClick}
                        style={{
                            position: 'relative',
                            height: '180px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                            e.currentTarget.querySelector('.intra-visual-card-bg').style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                            e.currentTarget.querySelector('.intra-visual-card-bg').style.transform = 'scale(1)';
                        }}
                    >
                        {/* Background Image */}
                        <div
                            className="intra-visual-card-bg"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${item.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                transition: 'transform 0.5s ease',
                                zIndex: 1
                            }}
                        />



                        {/* Content */}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            padding: '4px',
                            zIndex: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                                <span style={{
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    color: '#000000',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    padding: '6px 14px',
                                    borderRadius: '20px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}>
                                    {item.subtitle}
                                </span>
                                {item.value !== undefined && item.value !== null && (
                                    <span style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        color: '#000000',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                    }}>
                                        {item.value}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* FILA 3: Cumpleanos | Eventos | Encuestas */}
            <div className="intra-dashboard-grid-new">
                {/* FILA 3: Cumpleanos | Eventos | Encuestas */}
                <section className="intra-panel">
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Cumpleaños</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("calendario")}
                        >
                            Ver calendario
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
                        {cumpleanos.length === 0 ? (
                            <p className="intra-avisos-empty">
                                No hay cumpleaños próximos registrados.
                            </p>
                        ) : (
                            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                {/* Arrow Left */}
                                <button
                                    onClick={handlePrevBirthday}
                                    style={{
                                        position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
                                        width: '36px', height: '36px', cursor: 'pointer',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: '#64748b',
                                        fontWeight: 'bold', fontSize: '20px', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', zIndex: 20
                                    }}
                                >
                                    ‹
                                </button>

                                {cumpleanos.map((cumple, idx) => {
                                    // Determinar si animamos (usando ID) o no (usando idx por duplicados)
                                    const canAnimate = cumpleanosData.length >= 3;
                                    const key = canAnimate ? cumple.id : idx;

                                    // Styles Reverted to Carousel Layout with Performance Hints
                                    let style = {};
                                    if (idx === 0) { // Left
                                        style = {
                                            left: '20%',
                                            zIndex: 5,
                                            opacity: 0.6,
                                            transform: 'translate(-50%, -50%) scale(0.85)',
                                            filter: 'grayscale(60%)'
                                        };
                                    } else if (idx === 1) { // Center
                                        style = {
                                            left: '50%',
                                            zIndex: 10,
                                            opacity: 1,
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            filter: 'none'
                                        };
                                    } else { // Right
                                        style = {
                                            left: '80%',
                                            zIndex: 5,
                                            opacity: 0.6,
                                            transform: 'translate(-50%, -50%) scale(0.85)',
                                            filter: 'grayscale(60%)'
                                        };
                                    }

                                    const isCenter = idx === 1;

                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                // Revert to variable left
                                                width: '180px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                cursor: isCenter ? 'pointer' : 'default', // Add cursor pointer

                                                // Specific transitions including Left
                                                transition: 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.6s ease, filter 0.6s ease',
                                                willChange: 'left, transform, opacity',

                                                ...style
                                            }}
                                            onClick={() => {
                                                if (isCenter) {
                                                    // Convert birthday object to event format for Modal
                                                    const fechaEvento = new Date(); // Start with today/now

                                                    // Validate and set date components
                                                    if (cumple.mes !== undefined && cumple.mes !== null && !isNaN(cumple.mes) &&
                                                        cumple.dia && !isNaN(cumple.dia)) {
                                                        fechaEvento.setMonth(cumple.mes);
                                                        fechaEvento.setDate(cumple.dia);
                                                    }

                                                    fechaEvento.setHours(9, 0, 0, 0);

                                                    // Final safety check
                                                    if (isNaN(fechaEvento.getTime())) {
                                                        console.error("Invalid date for birthday:", cumple);
                                                        return;
                                                    }

                                                    const eventPayload = {
                                                        titulo: `Cumpleaños de ${cumple.titulo} `,
                                                        fechaInicio: fechaEvento.toISOString(),
                                                        fechaFin: fechaEvento.toISOString(), // Same day
                                                        categoria: "Cumpleaños",
                                                        descripcion: `¡Celebra el cumpleaños de ${cumple.titulo} !`,
                                                        responsable: cumple.titulo, // Show person name as responsible/user
                                                        ubicacion: "Oficina Central"
                                                    };
                                                    setSelectedEvento(eventPayload);
                                                }
                                            }}
                                        >
                                            {/* Unified Avatar Container - Morphable */}
                                            <div style={{
                                                width: isCenter ? '100px' : '60px',
                                                height: isCenter ? '100px' : '60px',
                                                borderRadius: '50%',
                                                padding: isCenter ? '4px' : '0px',
                                                background: isCenter ? 'linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary), white 40%) 100%)' : 'transparent',
                                                boxShadow: isCenter ? '0 10px 25px color-mix(in srgb, var(--color-primary), transparent 70%)' : '0 4px 10px rgba(0,0,0,0.1)',
                                                marginBottom: isCenter ? '12px' : '8px',
                                                transition: 'all 0.6s ease', // Smooth morph
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <img
                                                    src={getImageUrl(cumple.fotoPerfil) || DEFAULT_AVATAR}
                                                    alt={cumple.titulo}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '50%',
                                                        border: isCenter ? '4px solid white' : '0px solid white',
                                                        backgroundColor: '#fff',
                                                        transition: 'all 0.6s ease'
                                                    }}
                                                    onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                                />
                                            </div>

                                            {/* Name - Fade In/Out + Slide */}
                                            <div style={{
                                                height: isCenter ? 'auto' : '0px',
                                                opacity: isCenter ? 1 : 0,
                                                overflow: 'hidden',
                                                transition: 'all 0.6s ease',
                                                marginBottom: isCenter ? '4px' : '0px',
                                                transform: isCenter ? 'translateY(0)' : 'translateY(10px)'
                                            }}>
                                                <h4 style={{
                                                    fontSize: '1.rem',
                                                    fontWeight: '700',
                                                    color: '#1e293b',
                                                    textAlign: 'center',
                                                    lineHeight: '1.2'
                                                }}>
                                                    {cumple.titulo}
                                                </h4>
                                                <div style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>Cumpleaños</div>
                                            </div>

                                            {/* Date Badge - Morphable */}
                                            <div style={{
                                                fontSize: isCenter ? '0.8rem' : '0.75rem',
                                                fontWeight: '600',
                                                color: isCenter ? '#fff' : '#64748b',
                                                backgroundColor: isCenter ? 'var(--color-primary)' : 'transparent',
                                                padding: isCenter ? '4px 12px' : '0px',
                                                borderRadius: '20px',
                                                transition: 'all 0.6s ease',
                                                marginTop: isCenter ? '4px' : '0px'
                                            }}>
                                                {isCenter ? `${cumple.dia} ${MESES[cumple.mes]} ` : cumple.nombre}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Arrow Right */}
                                <button
                                    onClick={handleNextBirthday}
                                    style={{
                                        position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
                                        width: '36px', height: '36px', cursor: 'pointer',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: '#64748b',
                                        fontWeight: 'bold', fontSize: '20px', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', zIndex: 20
                                    }}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                <section className="intra-panel">
                    <div className="intra-panel-header">
                        <h3 className="intra-panel-title">Encuestas activas</h3>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("encuestas")}
                        >
                            Ver todas
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '4px 5px 4px 4px', maxHeight: '344px', overflowY: 'auto' }}>
                        {encuestasRecientes.length === 0 ? (
                            <p className="intra-avisos-empty">
                                No hay encuestas registradas.
                            </p>
                        ) : (
                            encuestasRecientes.map((encuesta) => {
                                const isRespondida = encuesta.status === "contestada";
                                const isCerrada = encuesta.status === "cerrada";
                                const bg = isRespondida ? '#15803d' : isCerrada ? '#64748b' : '#3b82f6';

                                return (
                                    <article
                                        key={encuesta.id}
                                        onClick={() => setSelectedEncuesta(encuesta)}
                                        style={{
                                            display: 'flex',
                                            backgroundColor: isRespondida ? '#f0fdf4' : '#fff',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                            cursor: 'pointer',
                                            height: '50px',
                                            flexShrink: 0,
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            border: `1px solid ${bg}`
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {/* Pestañita de estado */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '30px',
                                            height: '30px',
                                            zIndex: 2,
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: 0,
                                                height: 0,
                                                borderStyle: 'solid',
                                                borderWidth: '0 30px 30px 0',
                                                borderColor: `transparent ${bg} transparent transparent`,
                                            }} />
                                            <div style={{
                                                position: 'absolute',
                                                top: '2px',
                                                right: '4px',
                                                color: 'white',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                zIndex: 3,
                                                display: 'flex'
                                            }} title={`Estatus: ${encuesta.status}`}>
                                                {isRespondida ? '✓' : isCerrada ? '✕' : '🕒'}
                                            </div>
                                        </div>

                                        <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                                            <h3 style={{
                                                margin: '0',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#111827',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: '1.2',
                                                paddingRight: '15px'
                                            }}>
                                                {encuesta.titulo || encuesta.nombre}
                                            </h3>
                                        </div>
                                    </article>
                                );
                            })
                        )}
                    </div>
                </section>

                <section className="intra-panel">
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Proximos eventos</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("calendario")}
                        >
                            Ver calendario
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                        {eventosProximos.length === 0 ? (
                            <p className="intra-avisos-empty">
                                No hay eventos próximos registrados.
                            </p>
                        ) : (
                            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                {/* Arrow Left */}
                                <button
                                    onClick={handlePrevEvent}
                                    disabled={eventosIndex === 0}
                                    style={{
                                        position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
                                        width: '36px', height: '36px',
                                        cursor: eventosIndex === 0 ? 'default' : 'pointer',
                                        opacity: eventosIndex === 0 ? 0.3 : 1,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: '#64748b',
                                        fontWeight: 'bold', fontSize: '20px', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', zIndex: 20
                                    }}
                                >
                                    ‹
                                </button>

                                {eventosCarouselDisplay.map((evento, idx) => {
                                    if (!evento) return null;
                                    // const key removed to avoid redeclaration, using the one below
                                    const { dia, mes } = parseDiaMes(evento.fechaInicio || evento.fecha);
                                    const horario = evento.horario || formatTimeRange(evento.fechaInicio, evento.fechaFin);
                                    const catStyles = getCategoriaStyles(evento.categoria);

                                    // Determine if we animate (using ID) or idx
                                    const canAnimate = eventosProximos.length >= 3;
                                    const key = canAnimate ? evento.id : idx; // Use unique ID for morphing if possible

                                    // Style Logic - Matching Birthday Carousel
                                    let style = {};
                                    if (idx === 0) { // Left
                                        style = {
                                            left: '20%',
                                            zIndex: 5,
                                            opacity: 0.6,
                                            transform: 'translate(-50%, -50%) scale(0.85)',
                                            filter: 'grayscale(60%)'
                                        };
                                    } else if (idx === 1) { // Center
                                        style = {
                                            left: '50%',
                                            zIndex: 10,
                                            opacity: 1,
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            filter: 'none'
                                        };
                                    } else { // Right
                                        style = {
                                            left: '80%',
                                            zIndex: 5,
                                            opacity: 0.6,
                                            transform: 'translate(-50%, -50%) scale(0.85)',
                                            filter: 'grayscale(60%)'
                                        };
                                    }

                                    const isCenter = idx === 1;

                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                width: '200px', // Slightly wider for event details
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                cursor: isCenter ? 'pointer' : 'default', // Add cursor pointer

                                                // MATCHING TRANSITIONS
                                                transition: 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.6s ease, filter 0.6s ease',
                                                willChange: 'left, transform, opacity',
                                                ...style
                                            }}
                                            onClick={() => isCenter && setSelectedEvento({ ...evento })}
                                        >
                                            {/* Date Circle/Box - Morphable */}
                                            <div style={{
                                                width: isCenter ? '90px' : '60px',
                                                height: isCenter ? '90px' : '60px',
                                                borderRadius: '20%',
                                                backgroundColor: '#fff',
                                                border: isCenter ? '3px solid var(--color-primary)' : '1px solid #e2e8f0',
                                                boxShadow: isCenter ? '0 10px 25px color-mix(in srgb, var(--color-primary), transparent 70%)' : '0 4px 10px rgba(0,0,0,0.1)',
                                                marginBottom: isCenter ? '12px' : '8px',
                                                transition: 'all 0.6s ease', // Smooth morph
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: isCenter ? 'var(--color-primary)' : '#64748b'
                                            }}>
                                                <span style={{ fontSize: isCenter ? '2rem' : '1.2rem', fontWeight: '800', lineHeight: 1, transition: 'all 0.6s ease' }}>{dia}</span>
                                                <span style={{ fontSize: isCenter ? '0.9rem' : '0.7rem', fontWeight: '600', textTransform: 'uppercase', transition: 'all 0.6s ease' }}>{mes}</span>
                                            </div>

                                            {/* Title & Info - Fade In/Out + Slide */}
                                            <div style={{
                                                height: isCenter ? 'auto' : '0px',
                                                opacity: isCenter ? 1 : 0,
                                                overflow: 'hidden',
                                                transition: 'all 0.6s ease',
                                                marginBottom: isCenter ? '4px' : '0px',
                                                transform: isCenter ? 'translateY(0)' : 'translateY(10px)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                width: '100%'
                                            }}>
                                                <h4 style={{
                                                    fontSize: '1rem',
                                                    fontWeight: '700',
                                                    color: '#1e293b',
                                                    textAlign: 'center',
                                                    lineHeight: '1.2',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    marginBottom: '4px'
                                                }}>
                                                    {evento.titulo}
                                                </h4>

                                                {/* Category Pill with Color */}
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: catStyles.text,
                                                    backgroundColor: catStyles.bg,
                                                    border: `1px solid ${catStyles.border} 40`,
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    marginBottom: '6px',
                                                    fontWeight: '600'
                                                }}>
                                                    {evento.categoria || "Evento"}
                                                </div>

                                                {horario && (
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: '#64748b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        🕒 {horario}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Arrow Right */}
                                <button
                                    onClick={handleNextEvent}
                                    disabled={eventosIndex >= eventosProximos.length - 1}
                                    style={{
                                        position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
                                        width: '36px', height: '36px',
                                        cursor: eventosIndex >= eventosProximos.length - 1 ? 'default' : 'pointer',
                                        opacity: eventosIndex >= eventosProximos.length - 1 ? 0.3 : 1,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: '#64748b',
                                        fontWeight: 'bold', fontSize: '20px', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', zIndex: 20
                                    }}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </div >

            {/* NEW GRID LAYOUT - Redistribuido segun nuevo mockup */}
            < div className="intra-dashboard-grid-new" >
                {/* FILA 1: Avisos | Articulos | Noticias */}
                <section className="intra-panel" style={{ minWidth: 0 }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Avisos urgentes</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("avisos")}
                        >
                            Ver todos
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '460px', overflow: 'hidden', justifyContent: 'center', minWidth: 0 }}>
                        {avisosOrdenados.length === 0 ? (
                            <p className="intra-avisos-empty" style={{ margin: 0, textAlign: 'center' }}>
                                No hay avisos registrados todavía.
                            </p>
                        ) : (
                            <>
                                <button
                                    disabled={avisosIndex === 0}
                                    onClick={() => {
                                        setAvisosDir('prev');
                                        handlePrevPage(setAvisosIndex, avisosIndex);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: avisosIndex === 0 ? 'default' : 'pointer',
                                        opacity: avisosIndex === 0 ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(-90deg)', display: 'block' }}>❯</span>
                                </button>
                                <div style={{ overflow: 'hidden', height: '348px', position: 'relative', flexShrink: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                            padding: '8px 0',
                                            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            transform: `translateY(-${(avisosIndex / 3) * 348}px)`
                                        }}
                                    >
                                        {avisosOrdenados.map((aviso) => (
                                            <article
                                                key={aviso.id}
                                                onClick={() => setSelectedAviso(aviso)}
                                                style={{
                                                    display: 'flex',
                                                    backgroundColor: (aviso.destacada) ? '#fffbeb' : (String(aviso.nivel).toLowerCase() === 'alta' ? '#fef2f2' : String(aviso.nivel).toLowerCase() === 'media' ? '#fffbeb' : '#f0f9ff'),
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    cursor: 'pointer',
                                                    height: '100px',
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s',
                                                    position: 'relative',
                                                    border: aviso.destacada ? '1px solid #fbbf24' : `1px solid ${String(aviso.nivel).toLowerCase() === 'alta' ? '#ef4444' : String(aviso.nivel).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'}`
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                                }}
                                            >
                                                {/* Pestañita de urgencia */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: '40px',
                                                    height: '40px',
                                                    zIndex: 2,
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        width: 0,
                                                        height: 0,
                                                        borderStyle: 'solid',
                                                        borderWidth: '0 40px 40px 0',
                                                        borderColor: `transparent ${String(aviso.nivel).toLowerCase() === 'alta' ? '#ef4444' : String(aviso.nivel).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'} transparent transparent`,
                                                    }} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '3px',
                                                        right: '5px',
                                                        color: 'white',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        zIndex: 3
                                                    }} title={`Prioridad: ${aviso.nivel}`}>
                                                        {String(aviso.nivel).toLowerCase() === 'alta' ? 'A' : String(aviso.nivel).toLowerCase() === 'media' ? 'M' : 'B'}
                                                    </div>
                                                </div>

                                                <div style={{ width: '130px', flexShrink: 0 }}>
                                                    <img
                                                        src={getImageUrl(aviso.imagen) || PLACEHOLDER_IMAGE}
                                                        alt={aviso.titulo}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = PLACEHOLDER_IMAGE;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        padding: "8px 15px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div>
                                                        <h3
                                                            style={{
                                                                margin: "0 0 4px 0",
                                                                fontSize: "0.9rem",
                                                                fontWeight: "700",
                                                                color: "#1e293b",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                                lineHeight: "1.2",
                                                            }}
                                                        >
                                                            {aviso.titulo}
                                                        </h3>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '0.75rem',
                                                            color: '#64748b',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            {aviso.resumen || aviso.mensaje?.substring(0, 100) || aviso.contenido?.substring(0, 100)}
                                                        </p>
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            fontSize: "0.7rem",
                                                            color: "#94a3b8",
                                                            marginTop: '4px',
                                                            paddingTop: '4px',
                                                            borderTop: '1px solid rgba(0,0,0,0.05)'
                                                        }}
                                                    >
                                                        <span>👤 {aviso.autor?.split(' ')[0] || "Sistema"}</span>
                                                        <span>📅 {formatFechaCorta(aviso.fechaInicio || aviso.fecha)}</span>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    disabled={avisosIndex + 3 >= avisosOrdenados.length}
                                    onClick={() => {
                                        setAvisosDir('next');
                                        handleNextPage(setAvisosIndex, avisosIndex, avisosOrdenados.length);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: avisosIndex + 3 >= avisosOrdenados.length ? 'default' : 'pointer',
                                        opacity: avisosIndex + 3 >= avisosOrdenados.length ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(90deg)', display: 'block' }}>❯</span>
                                </button>
                            </>
                        )}
                    </div>
                </section >

                <section className="intra-panel" style={{ minWidth: 0 }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Articulos recientes</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("articulos")}
                        >
                            Ver todos
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '460px', overflow: 'hidden', justifyContent: 'center', minWidth: 0 }}>
                        {articulosOrdenados.length === 0 ? (
                            <p className="intra-avisos-empty" style={{ margin: 0, textAlign: 'center' }}>
                                No hay artículos registrados todavía.
                            </p>
                        ) : (
                            <>
                                <button
                                    disabled={articulosIndex === 0}
                                    onClick={() => {
                                        setArticulosDir('prev');
                                        handlePrevPage(setArticulosIndex, articulosIndex);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: articulosIndex === 0 ? 'default' : 'pointer',
                                        opacity: articulosIndex === 0 ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(-90deg)', display: 'block' }}>❯</span>
                                </button>
                                <div style={{ overflow: 'hidden', height: '348px', position: 'relative', flexShrink: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                            padding: '8px 0',
                                            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            transform: `translateY(-${(articulosIndex / 3) * 348}px)`
                                        }}
                                    >
                                        {articulosOrdenados.map((articulo) => (
                                            <article
                                                key={articulo.id}
                                                onClick={() => setSelectedArticulo(articulo)}
                                                style={{
                                                    display: 'flex',
                                                    position: 'relative',
                                                    backgroundColor: articulo.destacado ? '#fffbeb' : '#f0f9ff',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    cursor: 'pointer',
                                                    height: '100px',
                                                    flexShrink: 0,
                                                    transition: 'transform 0.2s',
                                                    border: articulo.destacado ? '1px solid #fbbf24' : '1px solid #3b82f6',
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                            >
                                                {/* Pestañita de tipo/importancia */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: '40px',
                                                    height: '40px',
                                                    zIndex: 2,
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        width: 0,
                                                        height: 0,
                                                        borderStyle: 'solid',
                                                        borderWidth: '0 40px 40px 0',
                                                        borderColor: `transparent ${articulo.destacado ? '#fbbf24' : '#3b82f6'} transparent transparent`,
                                                    }} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: articulo.destacado ? '2px' : '3px',
                                                        right: '5px',
                                                        color: 'white',
                                                        fontSize: articulo.destacado ? '1rem' : '0.75rem',
                                                        fontWeight: 'bold',
                                                        zIndex: 3
                                                    }} title={articulo.destacado ? "Artículo Destacado" : "Artículo Reciente"}>
                                                        {articulo.destacado ? '★' : 'A'}
                                                    </div>
                                                </div>
                                                <div style={{ width: '130px', flexShrink: 0 }}>
                                                    <img
                                                        src={getImageUrl(articulo.imagen) || PLACEHOLDER_IMAGE}
                                                        alt={articulo.titulo}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = PLACEHOLDER_IMAGE;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        padding: "8px 15px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div>
                                                        <h3
                                                            style={{
                                                                margin: "0 0 4px 0",
                                                                fontSize: "0.9rem",
                                                                fontWeight: "700",
                                                                color: "#1e293b",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                                lineHeight: "1.2",
                                                            }}
                                                        >
                                                            {articulo.titulo}
                                                        </h3>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '0.75rem',
                                                            color: '#64748b',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            {articulo.resumen || articulo.contenido?.substring(0, 100)}
                                                        </p>
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            fontSize: "0.7rem",
                                                            color: "#94a3b8",
                                                            marginTop: '4px',
                                                            paddingTop: '4px',
                                                            borderTop: '1px solid rgba(0,0,0,0.05)'
                                                        }}
                                                    >
                                                        <span>👤 {articulo.autor?.split(' ')[0] || "Sistema"}</span>
                                                        <span>📅 {formatFechaCorta(articulo.fechaPublicacion || articulo.fecha)}</span>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    disabled={articulosIndex + 3 >= articulosOrdenados.length}
                                    onClick={() => {
                                        setArticulosDir('next');
                                        handleNextPage(setArticulosIndex, articulosIndex, articulosOrdenados.length);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: articulosIndex + 3 >= articulosOrdenados.length ? 'default' : 'pointer',
                                        opacity: articulosIndex + 3 >= articulosOrdenados.length ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(90deg)', display: 'block' }}>❯</span>
                                </button>
                            </>
                        )}

                    </div >
                </section >

                <section className="intra-panel" style={{ minWidth: 0 }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Noticias recientes</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("noticias")}
                        >
                            Ver todas
                        </button>
                    </div>
                    <div className="intra-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '460px', overflow: 'hidden', justifyContent: 'center', minWidth: 0 }}>
                        {noticiasOrdenadas.length === 0 ? (
                            <p className="intra-avisos-empty" style={{ margin: 0, textAlign: 'center' }}>
                                No hay noticias registradas todavía.
                            </p>
                        ) : (
                            <>
                                <button
                                    disabled={noticiasIndex === 0}
                                    onClick={() => {
                                        setNoticiasDir('prev');
                                        handlePrevPage(setNoticiasIndex, noticiasIndex);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: noticiasIndex === 0 ? 'default' : 'pointer',
                                        opacity: noticiasIndex === 0 ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(-90deg)', display: 'block' }}>❯</span>
                                </button>
                                <div style={{ overflow: 'hidden', height: '348px', position: 'relative', flexShrink: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                            padding: '8px 0',
                                            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            transform: `translateY(-${(noticiasIndex / 3) * 348}px)`
                                        }}
                                    >
                                        {noticiasOrdenadas.map((noticia) => (
                                            <article
                                                key={noticia.id}
                                                onClick={() => setSelectedNoticia(noticia)}
                                                style={{
                                                    display: 'flex',
                                                    backgroundColor: (noticia.destacada) ? '#fffbeb' : (String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#fef2f2' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? '#fffbeb' : '#eff6ff'),
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    cursor: 'pointer',
                                                    height: '100px',
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s',
                                                    position: 'relative',
                                                    border: noticia.destacada ? '1px solid #fbbf24' : `1px solid ${String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#ef4444' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'}`
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                                }}
                                            >
                                                {/* Pestañita de urgencia */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: '40px',
                                                    height: '40px',
                                                    zIndex: 2,
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        width: 0,
                                                        height: 0,
                                                        borderStyle: 'solid',
                                                        borderWidth: '0 40px 40px 0',
                                                        borderColor: `transparent ${String(noticia.tipoNoticia).toLowerCase() === 'alta' ? '#ef4444' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? '#f59e0b' : '#3b82f6'} transparent transparent`,
                                                    }} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '3px',
                                                        right: '5px',
                                                        color: 'white',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        zIndex: 3
                                                    }} title={`Prioridad: ${noticia.tipoNoticia || 'Baja'}`}>
                                                        {String(noticia.tipoNoticia).toLowerCase() === 'alta' ? 'A' : String(noticia.tipoNoticia).toLowerCase() === 'media' ? 'M' : 'B'}
                                                    </div>
                                                </div>

                                                <div style={{ width: '130px', flexShrink: 0 }}>
                                                    <img
                                                        src={getImageUrl(noticia.imagen) || PLACEHOLDER_IMAGE}
                                                        alt={noticia.titulo}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = PLACEHOLDER_IMAGE;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        padding: "8px 15px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div>
                                                        <h3
                                                            style={{
                                                                margin: "0 0 4px 0",
                                                                fontSize: "0.9rem",
                                                                fontWeight: "700",
                                                                color: "#1e293b",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                                lineHeight: "1.2",
                                                            }}
                                                        >
                                                            {noticia.titulo}
                                                        </h3>
                                                        <p style={{
                                                            margin: 0,
                                                            fontSize: '0.75rem',
                                                            color: '#64748b',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            {noticia.resumen || noticia.contenido?.substring(0, 100)}
                                                        </p>
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            fontSize: "0.7rem",
                                                            color: "#94a3b8",
                                                            marginTop: '4px',
                                                            paddingTop: '4px',
                                                            borderTop: '1px solid rgba(0,0,0,0.05)'
                                                        }}
                                                    >
                                                        <span>👤 {noticia.autor?.split(' ')[0] || "Sistema"}</span>
                                                        <span>📅 {formatFechaCorta(noticia.fechaPublicacion || noticia.fecha)}</span>
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    disabled={noticiasIndex + 3 >= noticiasOrdenadas.length}
                                    onClick={() => {
                                        setNoticiasDir('next');
                                        handleNextPage(setNoticiasIndex, noticiasIndex, noticiasOrdenadas.length);
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: noticiasIndex + 3 >= noticiasOrdenadas.length ? 'default' : 'pointer',
                                        opacity: noticiasIndex + 3 >= noticiasOrdenadas.length ? 0.3 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: '0.2s',
                                        transform: 'scale(1)',
                                        flexShrink: 0,
                                        minWidth: 0
                                    }}
                                >
                                    <span style={{ fontSize: '1.5rem', color: '#333', transform: 'rotate(90deg)', display: 'block' }}>❯</span>
                                </button>
                            </>
                        )}
                    </div>
                </section>

                {/* FILA 2: Foros (full width) */}
                {/* FILA 2: Foros (Carousel Ranked) */}
                <section className="intra-panel intra-panel-full">
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Foros destacados</h2>
                        <button
                            className="intra-link-muted"
                            type="button"
                            onClick={() => handleNavegarA("foros")}
                        >
                            Ir a foros
                        </button>
                    </div>

                    <div className="intra-panel-content" style={{ position: 'relative', height: '280px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {forosCarousel.length === 0 ? (
                            <p className="intra-avisos-empty">No hay foros activos.</p>
                        ) : (
                            <>
                                {/* Left Arrow */}
                                <button
                                    onClick={handlePrevForum}
                                    style={{
                                        position: 'absolute', top: '50%', left: '20px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
                                        width: '40px', height: '40px', cursor: 'pointer', zIndex: 30,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: '24px', color: '#333'
                                    }}
                                >
                                    ‹
                                </button>

                                {/* Wrapped Carousel Items: Left, Center, Right logic */}
                                {[-1, 0, 1].map((offset) => {
                                    // Calculate actual index based on offset from center
                                    // relative index logic for circular buffer:
                                    // if offset = -1 (Left), index = (forumIndex - 1)
                                    // if offset = 0 (Center), index = forumIndex
                                    // if offset = 1 (Right), index = (forumIndex + 1)

                                    const len = forosCarousel.length;
                                    const rawIdx = (forumIndex + offset);
                                    const idx = (rawIdx % len + len) % len; // Proper modulo

                                    const foro = forosCarousel[idx];
                                    if (!foro) return null;

                                    const isCenter = offset === 0;

                                    // Styles Reverted to Carousel Layout
                                    let style = {};
                                    if (offset === -1) { // Left
                                        style = {
                                            left: '15%',
                                            zIndex: 5,
                                            opacity: 0.7,
                                            transform: 'translate(-50%, -50%) scale(0.8)',
                                            filter: 'blur(1px)'
                                        };
                                    } else if (offset === 0) { // Center
                                        style = {
                                            left: '50%',
                                            zIndex: 10,
                                            opacity: 1,
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            filter: 'none'
                                        };
                                    } else { // Right
                                        style = {
                                            left: '85%',
                                            zIndex: 5,
                                            opacity: 0.7,
                                            transform: 'translate(-50%, -50%) scale(0.8)',
                                            filter: 'blur(1px)'
                                        };
                                    }

                                    // Content Data
                                    let respuestas = 0;
                                    if (Array.isArray(foro.comentarios)) respuestas = foro.comentarios.length;
                                    else if (typeof foro.respuestas === "number") respuestas = foro.respuestas;

                                    return (
                                        <article
                                            key={`foro-carousel-${rawIdx}`}
                                            onClick={() => isCenter && setSelectedForo(foro)}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                // Responsive widths
                                                width: isCenter ? 'min(640px, 85%)' : 'min(300px, 20%)',
                                                height: isCenter ? '220px' : '160px',
                                                backgroundColor: foro.destacado ? '#fffbeb' : '#f0f9ff',
                                                borderRadius: '16px',
                                                boxShadow: isCenter ? '0 20px 40px rgba(15, 23, 42, 0.15)' : '0 10px 20px rgba(0,0,0,0.08)',

                                                // Transitions including Left and Width
                                                transition: 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.6s ease',
                                                willChange: 'left, width, transform, opacity',

                                                overflow: 'hidden',
                                                cursor: isCenter ? 'pointer' : 'default',
                                                display: 'flex',
                                                border: foro.destacado ? '2px solid #fbbf24' : '2px solid #3b82f6',
                                                ...style
                                            }}
                                        >
                                            {/* Pestañita de tipo/importancia (Ribbon) */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: isCenter ? '50px' : '40px',
                                                height: isCenter ? '50px' : '40px',
                                                zIndex: 20,
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: 0,
                                                    height: 0,
                                                    borderStyle: 'solid',
                                                    borderWidth: isCenter ? '0 50px 50px 0' : '0 40px 40px 0',
                                                    borderColor: `transparent ${foro.destacado ? '#fbbf24' : '#3b82f6'} transparent transparent`,
                                                }} />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: isCenter ? (foro.destacado ? '3px' : '5px') : (foro.destacado ? '2px' : '4px'),
                                                    right: isCenter ? '6px' : '5px',
                                                    color: 'white',
                                                    fontSize: isCenter ? (foro.destacado ? '1.2rem' : '0.9rem') : (foro.destacado ? '0.9rem' : '0.75rem'),
                                                    fontWeight: 'bold',
                                                    zIndex: 21
                                                }} title={foro.destacado ? "Foro Destacado" : "Foro Activo"}>
                                                    {foro.destacado ? '★' : 'F'}
                                                </div>
                                            </div>
                                            {/* IMAGE SECTION */}
                                            <div style={{
                                                width: isCenter ? '40%' : '100%',
                                                height: '100%',
                                                position: 'relative',
                                                backgroundColor: '#fff', // White background for potential logos
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden'
                                            }}>
                                                <img
                                                    src={getImageUrl(foro.imagen) || PLACEHOLDER_IMAGE}
                                                    alt={foro.titulo}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: isCenter ? 'contain' : 'cover', // contain for better logo view in center
                                                        padding: isCenter ? '10px' : '0'
                                                    }}
                                                    onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                                                />
                                                {/* Overlay Title for Side Items */}
                                                {!isCenter && (
                                                    <div style={{
                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                        background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                                                        padding: '10px 8px', color: '#fff'
                                                    }}>
                                                        <h4 style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{foro.titulo}</h4>
                                                    </div>
                                                )}
                                            </div>

                                            {/* CONTENT SECTION (Center Only) */}
                                            <div style={{
                                                width: isCenter ? '60%' : '0%',
                                                padding: isCenter ? '20px' : '0px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                opacity: isCenter ? 1 : 0,
                                                transition: 'opacity 0.3s ease 0.2s',
                                                backgroundColor: foro.destacado ? '#fffdf5' : '#f8fbff'
                                            }}>
                                                {isCenter && (
                                                    <>
                                                        <div className="intra-media-meta" style={{ marginBottom: '8px' }}>
                                                            <span className="intra-media-date" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                                                💬 {respuestas} comentarios
                                                            </span>
                                                        </div>
                                                        <h3 className="intra-media-title" style={{ fontSize: '1.2rem', marginBottom: '8px', lineHeight: 1.3, fontWeight: 800, color: '#1e293b' }}>
                                                            {foro.titulo}
                                                        </h3>
                                                        <p className="intra-media-description" style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', margin: 0 }}>
                                                            {foro.actualizado || foro.descripcion || "Participa en este debate destacado compartiendo tus ideas."}
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </article>

                                    );
                                })}

                                {/* Right Arrow */}
                                <button
                                    onClick={handleNextForum}
                                    style={{
                                        position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%)',
                                        background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
                                        width: '40px', height: '40px', cursor: 'pointer', zIndex: 30,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: '24px', color: '#333'
                                    }}
                                >
                                    ›
                                </button>
                            </>
                        )}
                    </div>
                </section>


            </div >

            {/* Modals */}
            < AvisoModal aviso={selectedAviso} onClose={() => setSelectedAviso(null)} />
            < NoticiaModal noticia={selectedNoticia} onClose={() => setSelectedNoticia(null)} />
            < ArticuloModal articulo={selectedArticulo} onClose={() => setSelectedArticulo(null)} />
            {
                selectedForo && (
                    <ForoCompletoModal
                        foro={selectedForo}
                        onClose={() => setSelectedForo(null)}
                    />
                )
            }
            {
                selectedEncuesta && (
                    <EncuestaModal
                        encuesta={selectedEncuesta}
                        onClose={() => setSelectedEncuesta(null)}
                        onEncuestaEnviada={() => {
                            setEncuestas(prev => prev.map(e =>
                                (e.idEncuesta === selectedEncuesta.idEncuesta || e.id === selectedEncuesta.id)
                                    ? { ...e, respondidaUsuario: true, respondida: true, status: 'contestada' }
                                    : e
                            ));
                            setSelectedEncuesta(null);
                        }}
                    />
                )
            }
            {
                selectedEvento && (
                    <EventoModal evento={selectedEvento} onClose={() => setSelectedEvento(null)} />
                )
            }
        </div >
    );
};

export default DashboardUsuario;
